import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Auth Service
export const authService = {
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  verifyMfa: async (userId, mfaCode) => {
    const response = await api.post('/auth/login/mfa', { 
      user_id: userId, 
      mfa_code: mfaCode 
    });
    return response.data;
  },

  getCurrentUser: async (token) => {
    const response = await api.get('/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
};

// Application Service
export const applicationService = {
  createApplication: async (data) => {
    const response = await api.post('/applications', data, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getMyApplication: async () => {
    const response = await api.get('/applications/my-application', {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  updateMyApplication: async (data) => {
    const response = await api.patch('/applications/my-application', data, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  submitApplication: async () => {
    const response = await api.post('/applications/my-application/submit', {}, {
      headers: getAuthHeader(),
    });
    return response.data;
  },
};

// Compliance Service
export const complianceService = {
  getChecklist: async () => {
    const response = await api.get('/compliance/checklist', {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getStatus: async () => {
    const response = await api.get('/compliance/status', {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  initializeChecklist: async () => {
    const response = await api.post('/compliance/initialize', {}, {
      headers: getAuthHeader(),
    });
    return response.data;
  },
};

// Document Service
export const documentService = {
  uploadDocument: async (file, documentType, opts = {}) => {
    // Backward compatible: opts may be a string (legacy expirationDate) or an options object.
    const options = typeof opts === 'string' ? { expirationDate: opts } : (opts || {});
    const { expirationDate = null, title = null, description = null, onProgress = null } = options;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);
    if (expirationDate) formData.append('expiration_date', expirationDate);
    if (title) formData.append('title', title);
    if (description) formData.append('description', description);

    const response = await api.post('/documents/upload', formData, {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (evt) => {
        if (onProgress && evt.total) {
          onProgress(Math.round((evt.loaded * 100) / evt.total));
        }
      },
    });
    return response.data;
  },

  getMyDocuments: async () => {
    const response = await api.get('/documents/my-documents', {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  downloadDocument: async (documentId) => {
    const response = await api.get(`/documents/${documentId}/download`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  deleteDocument: async (documentId) => {
    const response = await api.delete(`/documents/${documentId}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },
};

// Admin Service
export const adminService = {
  getDashboard: async () => {
    const response = await api.get('/admin/dashboard', {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getAllApplications: async (workflowState = null) => {
    const url = workflowState 
      ? `/admin/applications?workflow_state=${workflowState}`
      : '/admin/applications';
    const response = await api.get(url, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  reviewApplication: async (applicationId, action, notes) => {
    const response = await api.post(
      `/admin/applications/${applicationId}/review`,
      { action, notes },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  getAllUsers: async (role = null, workflowState = null) => {
    let url = '/admin/users?';
    if (role) url += `role=${role}&`;
    if (workflowState) url += `workflow_state=${workflowState}`;
    
    const response = await api.get(url, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  // NEW: Unified staff directory endpoint (SINGLE SOURCE OF TRUTH)
  getStaffDirectory: async (includeInactive = false, role = null) => {
    let url = `/admin/staff?include_inactive=${includeInactive}`;
    if (role) url += `&role=${role}`;
    
    const response = await api.get(url, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  createStaff: async (payload) =>
    (await api.post('/admin/staff', payload, { headers: getAuthHeader() })).data,
  updateStaff: async (userId, payload) =>
    (await api.patch(`/admin/staff/${userId}`, payload, { headers: getAuthHeader() })).data,
  inviteStaffPortal: async (userId) =>
    (await api.post(`/admin/staff/${userId}/invite`, {}, { headers: getAuthHeader() })).data,

  activateDriver: async (userId) => {
    const response = await api.post(
      `/admin/users/${userId}/activate`,
      {},
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  suspendDriver: async (userId, reason) => {
    const response = await api.post(
      `/admin/lifecycle/drivers/${userId}/suspend`,
      { reason },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  reactivateDriver: async (userId, notes = '') => {
    const response = await api.post(
      `/admin/lifecycle/drivers/${userId}/reactivate`,
      { notes },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  terminateDriver: async (userId, reason, terminationDate) => {
    const response = await api.post(
      `/admin/lifecycle/drivers/${userId}/terminate`,
      { reason, termination_date: terminationDate },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  getDriversAtRisk: async (days = 30) => {
    const response = await api.get(
      `/admin/lifecycle/drivers/at-risk?days=${days}`,
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  getAuditLogs: async (userId = null, action = null, limit = 100) => {
    let url = `/admin/audit-logs?limit=${limit}`;
    if (userId) url += `&user_id=${userId}`;
    if (action) url += `&action=${action}`;
    
    const response = await api.get(url, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  // Staff Suspension Management
  getSuspensionCategories: async () => {
    const response = await api.get('/admin/users/suspension-categories', {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  suspendUser: async (userId, category, notes = null) => {
    const response = await api.patch(
      `/admin/users/${userId}/suspend`,
      { category, notes },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  reactivateUser: async (userId) => {
    const response = await api.patch(
      `/admin/users/${userId}/reactivate`,
      {},
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  getSuspensionDetails: async (userId) => {
    const response = await api.get(
      `/admin/users/${userId}/suspension-details`,
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  getLinkedRecords: async (userId) => {
    const response = await api.get(
      `/admin/users/${userId}/linked-records`,
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  deleteUser: async (userId, confirmationEmail) => {
    const response = await api.delete(
      `/admin/users/${userId}`,
      { 
        headers: getAuthHeader(),
        data: { confirmation_email: confirmationEmail }
      }
    );
    return response.data;
  },
};

// Notification Service (Phase 3.5)
export const notificationService = {
  getMyNotifications: async (unreadOnly = false) => {
    const url = unreadOnly ? '/notifications/my?unread_only=true' : '/notifications/my';
    const response = await api.get(url, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  markAsRead: async (notificationId) => {
    const response = await api.post(
      `/notifications/my/${notificationId}/read`,
      {},
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  markAllAsRead: async () => {
    const response = await api.post(
      '/notifications/my/read-all',
      {},
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  getSettings: async () => {
    const response = await api.get('/notifications/settings', {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  updateSettings: async (settings) => {
    const response = await api.patch('/notifications/settings', settings, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getDeliveryHistory: async (status = null, limit = 100) => {
    let url = `/notifications/deliveries?limit=${limit}`;
    if (status) url += `&status=${status}`;
    const response = await api.get(url, {
      headers: getAuthHeader(),
    });
    return response.data;
  },
};

// Person File & Company File (CRM) + Founder Packet workflow
export const founderMaintenanceService = {
  legalStatus: async () => {
    const response = await api.get('/founder-maintenance/legal/status', { headers: getAuthHeader() });
    return response.data;
  },
  legalRegister: async () => {
    const response = await api.post('/founder-maintenance/legal/register', {}, { headers: getAuthHeader() });
    return response.data;
  },
  fixturesDryRun: async () => {
    const response = await api.post('/founder-maintenance/fixtures/dry-run', {}, { headers: getAuthHeader() });
    return response.data;
  },
  fixturesCleanup: async (confirmIds) => {
    const response = await api.post('/founder-maintenance/fixtures/cleanup', { confirm_ids: confirmIds || [] }, { headers: getAuthHeader() });
    return response.data;
  },
  previewFounderAccounts: async () => (await api.get('/founder-maintenance/preview-founder/accounts', { headers: getAuthHeader() })).data,
  resetPreviewFounder: async (email, new_password) => (await api.post('/founder-maintenance/preview-founder/reset-password', { email, new_password }, { headers: getAuthHeader() })).data,
};

export const personFileService = {
  listPeople: async (q = '') => {
    const response = await api.get(`/person-file/list${q ? `?q=${encodeURIComponent(q)}` : ''}`, { headers: getAuthHeader() });
    return response.data;
  },
  createPerson: async (data) => {
    const response = await api.post('/person-file', data, { headers: getAuthHeader() });
    return response.data;
  },
  getPerson: async (id) => {
    const response = await api.get(`/person-file/${id}`, { headers: getAuthHeader() });
    return response.data;
  },
  updatePerson: async (id, data) => {
    const response = await api.put(`/person-file/${id}`, data, { headers: getAuthHeader() });
    return response.data;
  },
  addDocument: async (id, formData) => {
    const response = await api.post(`/person-file/${id}/document`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  verifyDocument: async (id, docKey, action, note) => {
    const response = await api.post(`/person-file/${id}/document/${docKey}/verify`, { action, note }, { headers: getAuthHeader() });
    return response.data;
  },
  downloadDocument: async (id, docKey) => {
    const response = await api.get(`/onboarding/application/${id}/document/${docKey}/download`, { headers: getAuthHeader() });
    return response.data;
  },
  packetPreview: async (id) => {
    const response = await api.get(`/onboarding/application/${id}/packet-preview`, { headers: getAuthHeader() });
    return response.data;
  },
  sendSimulation: async (id) => {
    const response = await api.get(`/onboarding/application/${id}/send-simulation`, { headers: getAuthHeader() });
    return response.data;
  },
  packetReview: async (id, notes) => {
    const response = await api.post(`/onboarding/application/${id}/packet/review`, { notes }, { headers: getAuthHeader() });
    return response.data;
  },
  packetApprove: async (id, notes) => {
    const response = await api.post(`/onboarding/application/${id}/packet/approve`, { notes }, { headers: getAuthHeader() });
    return response.data;
  },
  packetSend: async (id, authorized, confirmSend) => {
    const response = await api.post(`/onboarding/application/${id}/packet/send`, { authorized, confirm_send: confirmSend }, { headers: getAuthHeader() });
    return response.data;
  },
  packetResend: async (id, reason) => {
    const response = await api.post(`/onboarding/application/${id}/packet/resend`, { authorized: true, reason }, { headers: getAuthHeader() });
    return response.data;
  },
  // Company files
  listCompanies: async (q = '') => {
    const response = await api.get(`/company-file/list${q ? `?q=${encodeURIComponent(q)}` : ''}`, { headers: getAuthHeader() });
    return response.data;
  },
  getAttention: async () => {
    const response = await api.get('/person-file/attention', { headers: getAuthHeader() });
    return response.data;
  },
  listLegalDocuments: async () => {
    const response = await api.get('/legal-documents', { headers: getAuthHeader() });
    return response.data;
  },
  downloadLegalDocument: async (docKey, fmt) => {
    const response = await api.get(`/legal-documents/${docKey}/download?fmt=${fmt}`, { headers: getAuthHeader() });
    return response.data;
  },
  approveLegalDocument: async (docKey) => {
    const response = await api.post(`/legal-documents/${docKey}/approve`, {}, { headers: getAuthHeader() });
    return response.data;
  },
  createCompany: async (data) => {
    const response = await api.post('/company-file', data, { headers: getAuthHeader() });
    return response.data;
  },
  getCompany: async (id) => {
    const response = await api.get(`/company-file/${id}`, { headers: getAuthHeader() });
    return response.data;
  },
  updateCompany: async (id, data) => {
    const response = await api.put(`/company-file/${id}`, data, { headers: getAuthHeader() });
    return response.data;
  },
  companyAddDocument: async (id, formData) => {
    const response = await api.post(`/company-file/${id}/document`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  companyVerifyDocument: async (id, docId, action, note) => {
    const response = await api.post(`/company-file/${id}/document/${docId}/verify`, { action, note }, { headers: getAuthHeader() });
    return response.data;
  },
  companyDownloadDocument: async (id, docId) => {
    const response = await api.get(`/company-file/${id}/document/${docId}/download`, { headers: getAuthHeader() });
    return response.data;
  },
};

// Settlement Service (Phase 3.5)
export const settlementService = {
  getDriverSettlements: async (driverId) => {
    const response = await api.get(`/settlements/driver/${driverId}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getSettlement: async (settlementId) => {
    const response = await api.get(`/settlements/${settlementId}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  createSettlement: async (data) => {
    const response = await api.post('/settlements', data, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  finalizeSettlement: async (settlementId) => {
    const response = await api.post(
      `/settlements/${settlementId}/finalize`,
      {},
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  getPaySchedule: async (driverId) => {
    const response = await api.get(`/settlements/driver/${driverId}/pay-schedule`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  exportFactoring: async () => {
    const response = await api.get('/settlements/export/factoring', {
      headers: getAuthHeader(),
      responseType: 'blob',
    });
    return response.data;
  },

  exportCSV: async () => {
    const response = await api.get('/settlements/export/csv', {
      headers: getAuthHeader(),
      responseType: 'blob',
    });
    return response.data;
  },
};

// Compliance Integrations Service (Phase 3.5)
export const complianceIntegrationsService = {
  getSummary: async (userId) => {
    const response = await api.get(`/integrations/summary/${userId}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  runAllChecks: async (userId) => {
    const response = await api.post(
      `/integrations/run-all/${userId}`,
      {},
      { headers: getAuthHeader() }
    );
    return response.data;
  },
};

// Export Service
export const exportService = {
  exportDQF: async (userId) => {
    const response = await api.get(`/exports/dqf/${userId}`, {
      headers: getAuthHeader(),
      responseType: 'blob',
    });
    return response.data;
  },

  exportComplianceSummary: async () => {
    const response = await api.get('/exports/compliance-summary', {
      headers: getAuthHeader(),
      responseType: 'blob',
    });
    return response.data;
  },

  exportSettlements: async () => {
    const response = await api.get('/exports/settlements', {
      headers: getAuthHeader(),
      responseType: 'blob',
    });
    return response.data;
  },
};

// Phase 4 - Feature Flags Service
export const featureFlagsService = {
  getAllFlags: async () => {
    const response = await api.get('/feature-flags', {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getFlagsByPhase: async (phase) => {
    const response = await api.get(`/feature-flags/phase/${phase}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  toggleFlag: async (flagKey, enabled, reason = null) => {
    const response = await api.post(
      `/feature-flags/${flagKey}/toggle`,
      { enabled, reason },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  getFlagHistory: async (flagKey, limit = 50) => {
    const response = await api.get(`/feature-flags/${flagKey}/history?limit=${limit}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getEnabledFeatures: async () => {
    const response = await api.get('/feature-flags/enabled/all', {
      headers: getAuthHeader(),
    });
    return response.data;
  },
};

// Phase 4 - Dispatch Service
export const dispatchService = {
  getDashboard: async () => {
    const response = await api.get('/dispatch/dashboard', {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getLoads: async (status = null, assigned = null) => {
    let url = '/dispatch/loads?';
    if (status) url += `status=${status}&`;
    if (assigned !== null) url += `assigned=${assigned}`;
    const response = await api.get(url, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  createLoad: async (data) => {
    const response = await api.post('/dispatch/loads', data, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getLoad: async (loadId) => {
    const response = await api.get(`/dispatch/loads/${loadId}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  assignDriver: async (loadId, driverId, notes = null) => {
    const response = await api.post(
      `/dispatch/loads/${loadId}/assign`,
      { driver_id: driverId, notes },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  unassignDriver: async (loadId) => {
    const response = await api.post(
      `/dispatch/loads/${loadId}/unassign`,
      {},
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  getDispatches: async (status = null, driverId = null) => {
    let url = '/dispatch/dispatches?';
    if (status) url += `status=${status}&`;
    if (driverId) url += `driver_id=${driverId}`;
    const response = await api.get(url, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  updateDispatchStatus: async (dispatchId, status, notes = null) => {
    const response = await api.post(
      `/dispatch/dispatches/${dispatchId}/status`,
      { status, notes },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  getAvailableDrivers: async () => {
    const response = await api.get('/dispatch/available-drivers', {
      headers: getAuthHeader(),
    });
    return response.data;
  },
};

// Phase 4 - Loads Service
export const loadsService = {
  createManualLoad: async (data) => {
    const response = await api.post('/loads/manual', data, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  searchLoadBoard: async (originState, destinationState, equipmentType = 'dry_van') => {
    const response = await api.get(
      `/loads/board/search?origin_state=${originState}&destination_state=${destinationState}&equipment_type=${equipmentType}`,
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  calculateProfit: async (data) => {
    const response = await api.post('/loads/profit/calculate', data, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getLoadProfit: async (loadId) => {
    const response = await api.get(`/loads/${loadId}/profit`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },
};

// Phase 4 - Tracking Service
export const trackingService = {
  updateLocation: async (latitude, longitude, heading = null, speed = null) => {
    const response = await api.post(
      '/tracking/gps/update',
      { latitude, longitude, heading, speed },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  getDriverLocation: async (driverId) => {
    const response = await api.get(`/tracking/gps/driver/${driverId}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getAllDriverLocations: async () => {
    const response = await api.get('/tracking/gps/all', {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getLocationHistory: async (driverId, hours = 24) => {
    const response = await api.get(`/tracking/gps/driver/${driverId}/history?hours=${hours}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getELDStatus: async (driverId) => {
    const response = await api.get(`/tracking/eld/driver/${driverId}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getDispatchETA: async (dispatchId) => {
    const response = await api.get(`/tracking/eta/dispatch/${dispatchId}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },
};

// Phase 4 - Analytics Service
export const analyticsService = {
  getLaneAnalytics: async (periodDays = 30, originState = null, destinationState = null) => {
    let url = `/analytics/lanes?period_days=${periodDays}`;
    if (originState) url += `&origin_state=${originState}`;
    if (destinationState) url += `&destination_state=${destinationState}`;
    const response = await api.get(url, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getTopLanes: async (limit = 10, sortBy = 'revenue', periodDays = 30) => {
    const response = await api.get(
      `/analytics/lanes/top?limit=${limit}&sort_by=${sortBy}&period_days=${periodDays}`,
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  getDriverProfitability: async (periodDays = 30, driverId = null) => {
    let url = `/analytics/drivers/profitability?period_days=${periodDays}`;
    if (driverId) url += `&driver_id=${driverId}`;
    const response = await api.get(url, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  exportDriverProfitability: async (periodDays = 30) => {
    const response = await api.get(`/analytics/drivers/profitability/export?period_days=${periodDays}`, {
      headers: getAuthHeader(),
      responseType: 'blob',
    });
    return response.data;
  },

  getLoadCostBreakdown: async (loadId) => {
    const response = await api.get(`/analytics/loads/${loadId}/cost-breakdown`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getSummary: async (periodDays = 30) => {
    const response = await api.get(`/analytics/summary?period_days=${periodDays}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },
};

// Phase 4 - Broker Service
export const brokerService = {
  createAccount: async (data) => {
    const response = await api.post('/broker/accounts', data, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getAccounts: async (activeOnly = true) => {
    const response = await api.get(`/broker/accounts?active_only=${activeOnly}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getAccount: async (brokerId) => {
    const response = await api.get(`/broker/accounts/${brokerId}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  updateAccount: async (brokerId, data) => {
    const response = await api.patch(`/broker/accounts/${brokerId}`, data, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  createTender: async (data) => {
    const response = await api.post('/broker/tenders', data, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getTenders: async (status = 'pending', brokerId = null) => {
    let url = `/broker/tenders?status_filter=${status}`;
    if (brokerId) url += `&broker_id=${brokerId}`;
    const response = await api.get(url, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  acceptTender: async (tenderId) => {
    const response = await api.post(
      `/broker/tenders/${tenderId}/accept`,
      {},
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  rejectTender: async (tenderId, reason = 'Not specified') => {
    const response = await api.post(
      `/broker/tenders/${tenderId}/reject?reason=${encodeURIComponent(reason)}`,
      {},
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  getDocuments: async (brokerId, loadId = null) => {
    let url = `/broker/documents/${brokerId}`;
    if (loadId) url += `?load_id=${loadId}`;
    const response = await api.get(url, {
      headers: getAuthHeader(),
    });
    return response.data;
  },
};

// ============================================
// Phase 5 - Staff Management Service
// ============================================

export const staffService = {
  getRoles: async () => {
    const response = await api.get('/staff/roles', {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  assignRole: async (data) => {
    const response = await api.post('/staff/assign', data, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  revokeRole: async (assignmentId, reason = 'Role revoked') => {
    const response = await api.delete(`/staff/assign/${assignmentId}?reason=${encodeURIComponent(reason)}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getAssignments: async (role = null, activeOnly = true) => {
    let url = `/staff/assignments?active_only=${activeOnly}`;
    if (role) url += `&role=${role}`;
    const response = await api.get(url, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getUserPermissions: async (userId) => {
    const response = await api.get(`/staff/user/${userId}/permissions`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },
};

// ============================================
// Phase 5 - Maintenance Service
// ============================================

export const maintenanceService = {
  // Vehicles
  createVehicle: async (data) => {
    const response = await api.post('/maintenance/vehicles', data, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getVehicles: async (statusFilter = null, vehicleType = null) => {
    let url = '/maintenance/vehicles?';
    if (statusFilter) url += `status_filter=${statusFilter}&`;
    if (vehicleType) url += `vehicle_type=${vehicleType}`;
    const response = await api.get(url, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getVehicle: async (vehicleId) => {
    const response = await api.get(`/maintenance/vehicles/${vehicleId}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  updateVehicle: async (vehicleId, data) => {
    const response = await api.patch(`/maintenance/vehicles/${vehicleId}`, data, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  updateVehicleStatus: async (vehicleId, status, reason, notes = null) => {
    const response = await api.post(
      `/maintenance/vehicles/${vehicleId}/status`,
      { status, reason, notes },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  getDispatchEligibleVehicles: async () => {
    const response = await api.get('/maintenance/vehicles/dispatch-eligible', {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  // Inspections
  createInspection: async (data) => {
    const response = await api.post('/maintenance/inspections', data, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getInspections: async (vehicleId = null, resultFilter = null, limit = 50) => {
    let url = `/maintenance/inspections?limit=${limit}`;
    if (vehicleId) url += `&vehicle_id=${vehicleId}`;
    if (resultFilter) url += `&result_filter=${resultFilter}`;
    const response = await api.get(url, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  // Issues
  createIssue: async (data) => {
    const response = await api.post('/maintenance/issues', data, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getIssues: async (vehicleId = null, statusFilter = null, priorityFilter = null) => {
    let url = '/maintenance/issues?';
    if (vehicleId) url += `vehicle_id=${vehicleId}&`;
    if (statusFilter) url += `status_filter=${statusFilter}&`;
    if (priorityFilter) url += `priority_filter=${priorityFilter}`;
    const response = await api.get(url, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  updateIssue: async (issueId, data) => {
    const response = await api.patch(`/maintenance/issues/${issueId}`, data, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  // Dashboard
  getDashboard: async () => {
    const response = await api.get('/maintenance/dashboard', {
      headers: getAuthHeader(),
    });
    return response.data;
  },
};

// ============================================
// Phase 5 - Accounting Service
// ============================================

export const accountingService = {
  // Settlements
  getSettlements: async (statusFilter = null, driverId = null, periodStart = null, periodEnd = null) => {
    let url = '/accounting/settlements?';
    if (statusFilter) url += `status_filter=${statusFilter}&`;
    if (driverId) url += `driver_id=${driverId}&`;
    if (periodStart) url += `period_start=${periodStart}&`;
    if (periodEnd) url += `period_end=${periodEnd}`;
    const response = await api.get(url, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getSettlementDetail: async (settlementId) => {
    const response = await api.get(`/accounting/settlements/${settlementId}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  finalizeSettlement: async (settlementId, notes = null) => {
    const response = await api.post(
      '/accounting/settlements/finalize',
      { settlement_id: settlementId, notes },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  markSettlementPaid: async (settlementId, paymentMethod, referenceNumber = null, checkNumber = null, transactionId = null, notes = null) => {
    const response = await api.post(
      '/accounting/settlements/mark-paid',
      {
        settlement_id: settlementId,
        payment_method: paymentMethod,
        reference_number: referenceNumber,
        check_number: checkNumber,
        transaction_id: transactionId,
        notes
      },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  // Reports
  getSummary: async (periodStart = null, periodEnd = null) => {
    let url = '/accounting/reports/summary?';
    if (periodStart) url += `period_start=${periodStart}&`;
    if (periodEnd) url += `period_end=${periodEnd}`;
    const response = await api.get(url, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  exportSettlements: async (statusFilter = null, periodStart = null, periodEnd = null) => {
    let url = '/accounting/exports/settlements?';
    if (statusFilter) url += `status_filter=${statusFilter}&`;
    if (periodStart) url += `period_start=${periodStart}&`;
    if (periodEnd) url += `period_end=${periodEnd}`;
    const response = await api.get(url, {
      headers: getAuthHeader(),
      responseType: 'blob',
    });
    return response.data;
  },

  exportPayments: async (statusFilter = null) => {
    let url = '/accounting/exports/payments?';
    if (statusFilter) url += `status_filter=${statusFilter}`;
    const response = await api.get(url, {
      headers: getAuthHeader(),
      responseType: 'blob',
    });
    return response.data;
  },

  // Dashboard
  getDashboard: async () => {
    const response = await api.get('/accounting/dashboard', {
      headers: getAuthHeader(),
    });
    return response.data;
  },
};

// Lead Service (Quote & Lead Management)
export const leadService = {
  // Slice 1 — public capture
  submitPublicLead: async (payload) => {
    const response = await api.post('/public/leads', payload);
    return response.data;
  },
  // Slice 2 — internal management (admin)
  listLeads: async (params = {}) => (await api.get('/leads', { headers: getAuthHeader(), params })).data,
  getLead: async (id) => (await api.get(`/leads/${id}`, { headers: getAuthHeader() })).data,
  updateLead: async (id, payload) => (await api.patch(`/leads/${id}`, payload, { headers: getAuthHeader() })).data,
  createLead: async (payload) => (await api.post('/leads', payload, { headers: getAuthHeader() })).data,
  getSources: async () => (await api.get('/leads/meta/sources', { headers: getAuthHeader() })).data,
  getAssignees: async () => (await api.get('/leads/meta/assignees', { headers: getAuthHeader() })).data,
  getStats: async () => (await api.get('/leads/stats/summary', { headers: getAuthHeader() })).data,
};

// Quote Service (Quote & Lead Management — Slice 3: quote generation; Slice 4: workflow)
export const quoteService = {
  listQuotes: async (params = {}) => (await api.get('/quotes', { headers: getAuthHeader(), params })).data,
  createQuote: async (payload) => (await api.post('/quotes', payload, { headers: getAuthHeader() })).data,
  getQuote: async (id) => (await api.get(`/quotes/${id}`, { headers: getAuthHeader() })).data,
  updateQuote: async (id, payload) => (await api.patch(`/quotes/${id}`, payload, { headers: getAuthHeader() })).data,
  // Slice 4 — workflow
  sendQuote: async (id) => (await api.post(`/quotes/${id}/send`, {}, { headers: getAuthHeader() })).data,
  reviseQuote: async (id) => (await api.post(`/quotes/${id}/revise`, {}, { headers: getAuthHeader() })).data,
  createJobFromQuote: async (id) => (await api.post(`/quotes/${id}/create-job`, {}, { headers: getAuthHeader() })).data,
  recordActuals: async (id, payload) => (await api.post(`/quotes/${id}/actuals`, payload, { headers: getAuthHeader() })).data,
  setQuoteStatus: async (id, payload) => (await api.post(`/quotes/${id}/status`, payload, { headers: getAuthHeader() })).data,
  // Quote Cost Intelligence — route/fuel/equipment-fit/cost/pricing/warnings/eligibility (server-computed)
  evaluateIntelligence: async (payload) =>
    (await api.post('/quotes/intelligence/evaluate', payload, { headers: getAuthHeader() })).data,
  placesAutocomplete: async (q) =>
    (await api.get('/quotes/places/autocomplete', { params: { q }, headers: getAuthHeader() })).data,
  equipmentRecord: async (key) =>
    (await api.get(`/quotes/equipment/${key}`, { headers: getAuthHeader() })).data,
  updateEquipment: async (key, payload) =>
    (await api.patch(`/quotes/equipment/${key}`, payload, { headers: getAuthHeader() })).data,
};

// BLA-STD-007 Pricing Standard (read-only, deterministic) — Quote Builder assist
export const pricingService = {
  getStandard: async () => (await api.get('/quotes/pricing/standard', { headers: getAuthHeader() })).data,
  quotePreview: async (service_code, inputs = {}) =>
    (await api.post('/quotes/pricing/quote-preview', { service_code, inputs }, { headers: getAuthHeader() })).data,
  discountCheck: async (payload) =>
    (await api.post('/quotes/pricing/discount-check', payload, { headers: getAuthHeader() })).data,
  getReviews: async () => (await api.get('/quotes/pricing/reviews', { headers: getAuthHeader() })).data,
};

// Public Quote Portal (Slice 4) — read-only, token-secured, no auth
export const publicQuoteService = {
  getByToken: async (token) => (await api.get(`/public/quote/${token}`)).data,
};

// Sales Intelligence (Quote & Lead Management — Slice 5: Revenue & Sales Intelligence dashboard)
export const salesIntelligenceService = {
  getDashboard: async () => (await api.get('/sales-intelligence/dashboard', { headers: getAuthHeader() })).data,
  getFollowUp: async () => (await api.get('/sales-intelligence/follow-up', { headers: getAuthHeader() })).data,
};

// Follow-Up Activity Engine (Quote & Lead Management — Slice 6)
export const activityService = {
  listActivities: async (params = {}) => (await api.get('/activities', { headers: getAuthHeader(), params })).data,
  getStats: async () => (await api.get('/activities/stats', { headers: getAuthHeader() })).data,
  generate: async () => (await api.post('/activities/generate', {}, { headers: getAuthHeader() })).data,
  createActivity: async (payload) => (await api.post('/activities', payload, { headers: getAuthHeader() })).data,
  updateActivity: async (id, payload) => (await api.patch(`/activities/${id}`, payload, { headers: getAuthHeader() })).data,
};

// Activation Status (Quote & Lead — Soft Go-Live observability, read-only)
export const activationService = {
  getQuoteLeadStatus: async () => (await api.get('/feature-flags/activation-status/quote-lead', { headers: getAuthHeader() })).data,
};

// Universal Enterprise Search (Expansion Package I, Phase 2) — permission-aware, read-only
export const searchService = {
  isEnabled: async () => {
    try {
      const flags = await featureFlagsService.getEnabledFeatures();
      // /feature-flags/enabled/all returns a { key: boolean } dictionary.
      // Also tolerate array / { enabled: [...] } / { features: [...] } shapes.
      const matches = (arr) => arr.some((f) => (typeof f === 'string' ? f : f?.key) === 'universal_search_enabled');
      if (Array.isArray(flags)) return matches(flags);
      if (flags && typeof flags === 'object') {
        if (Array.isArray(flags.enabled)) return matches(flags.enabled);
        if (Array.isArray(flags.features)) return matches(flags.features);
        return flags['universal_search_enabled'] === true;
      }
      return false;
    } catch (e) {
      return false;
    }
  },
  search: async (q, { types = null, limit = 20, includeContext = false } = {}) => {
    const params = { q, limit, include_context: includeContext };
    if (types) params.types = types;
    const response = await api.get('/search', { headers: getAuthHeader(), params });
    return response.data;
  },
};

// Controlled Internal Integration Phase (Track 1) — read-only integration layer.
export const integrationService = {
  isEnabled: async () => {
    try {
      const flags = await featureFlagsService.getEnabledFeatures();
      if (flags && typeof flags === 'object' && !Array.isArray(flags)) {
        return flags['relationship_engine_enabled'] === true;
      }
      return false;
    } catch (e) {
      return false;
    }
  },
  getMap: async () => (await api.get('/integration/map', { headers: getAuthHeader() })).data,
  getRegistry: async (kind, { skip = 0, limit = 50 } = {}) =>
    (await api.get(`/integration/registry/${kind}`, { headers: getAuthHeader(), params: { skip, limit } })).data,
  getCustomer360: async (leadId) =>
    (await api.get(`/integration/customer/${leadId}/360`, { headers: getAuthHeader() })).data,
  searchCustomers: async (q, { limit = 10 } = {}) =>
    (await api.get('/integration/customer/search', { headers: getAuthHeader(), params: { q, limit } })).data,
  getTimeline: async (resourceId) =>
    (await api.get(`/integration/timeline/${resourceId}`, { headers: getAuthHeader() })).data,
  getOwnerDay: async () =>
    (await api.get('/integration/owner-day', { headers: getAuthHeader() })).data,
  getProviderCatalog: async (params = {}) =>
    (await api.get('/integration/providers', { headers: getAuthHeader(), params })).data,
  getProviderHealth: async () =>
    (await api.get('/integration/providers/health', { headers: getAuthHeader() })).data,
  getDocusignAuthorizeUrl: async () =>
    (await api.get('/integration/esign/docusign/authorize', { headers: getAuthHeader() })).data,
  checkDispatchEligibility: async (driverId, params = {}) =>
    (await api.get('/dispatch/eligibility', { headers: getAuthHeader(), params: { driver_id: driverId, ...params } })).data,
  verifyDriver: async (driverId, { equipment = null, qualification = null } = {}) =>
    (await api.post(`/dispatch/driver/${driverId}/verify`, { equipment, qualification }, { headers: getAuthHeader() })).data,
};

export const driveService = {
  verify: async () => (await api.get('/integration/drive/verify', { headers: getAuthHeader() })).data,
  inventory: async () => (await api.post('/integration/drive/inventory', {}, { headers: getAuthHeader() })).data,
  scan: async () => (await api.post('/integration/drive/scan', {}, { headers: getAuthHeader() })).data,
  getSchedule: async () => (await api.get('/integration/drive/schedule', { headers: getAuthHeader() })).data,
  getAlerts: async ({ status, severity, limit = 200 } = {}) =>
    (await api.get('/integration/drive/alerts', { headers: getAuthHeader(), params: { status, severity, limit } })).data,
  getDuplicatesReport: async () => (await api.get('/integration/drive/duplicates/report', { headers: getAuthHeader() })).data,
  getDuplicatesReview: async ({ limit = 10 } = {}) =>
    (await api.get('/integration/drive/duplicates/review', { headers: getAuthHeader(), params: { limit } })).data,
  getDigest: async () => (await api.get('/integration/drive/alerts/digest', { headers: getAuthHeader() })).data,
  getExpiringDocuments: async (days = 60) => (await api.get(`/integration/drive/documents/expiring?days=${days}`, { headers: getAuthHeader() })).data,
  insuranceExtract: async (mode = 'sample') =>
    (await api.post(`/integration/drive/insurance/extract?mode=${mode}`, {}, { headers: getAuthHeader() })).data,
  getStats: async () => (await api.get('/integration/drive/index/stats', { headers: getAuthHeader() })).data,
  getIndex: async ({ domain, doc_type, q, skip = 0, limit = 50 } = {}) =>
    (await api.get('/integration/drive/index', { headers: getAuthHeader(), params: { domain, doc_type, q, skip, limit } })).data,
  getDuplicates: async () => (await api.get('/integration/drive/duplicates', { headers: getAuthHeader() })).data,
  getChanges: async ({ limit = 100 } = {}) =>
    (await api.get('/integration/drive/changes', { headers: getAuthHeader(), params: { limit } })).data,
  getInsurance: async () => (await api.get('/integration/drive/insurance', { headers: getAuthHeader() })).data,
};

// Partner Portal — Founder/admin management (super_admin) + BlueLock-side request handoff (any admin)
export const partnerAdminService = {
  invite: async (payload) => (await api.post('/partner-admin/invitations', payload, { headers: getAuthHeader() })).data,
  activate: async (payload) => (await api.post('/partner-admin/activate', payload)).data,
  listUsers: async (company_id) => (await api.get('/partner-admin/users', { headers: getAuthHeader(), params: company_id ? { company_id } : {} })).data,
  getUser: async (id) => (await api.get(`/partner-admin/users/${id}`, { headers: getAuthHeader() })).data,
  deactivate: async (id) => (await api.post(`/partner-admin/users/${id}/deactivate`, {}, { headers: getAuthHeader() })).data,
  reactivate: async (id) => (await api.post(`/partner-admin/users/${id}/activate`, {}, { headers: getAuthHeader() })).data,
  revoke: async (id) => (await api.post(`/partner-admin/users/${id}/revoke`, {}, { headers: getAuthHeader() })).data,
  updatePermissions: async (id, permissions) => (await api.patch(`/partner-admin/users/${id}/permissions`, { permissions }, { headers: getAuthHeader() })).data,
  getActivity: async (id) => (await api.get(`/partner-admin/users/${id}/activity`, { headers: getAuthHeader() })).data,
  permissionsCatalog: async () => (await api.get('/partner-admin/permissions/catalog', { headers: getAuthHeader() })).data,
  // BlueLock-side requests
  listRequests: async (params = {}) => (await api.get('/partner-admin/requests', { headers: getAuthHeader(), params })).data,
  getRequest: async (id) => (await api.get(`/partner-admin/requests/${id}`, { headers: getAuthHeader() })).data,
  getQuotePrefill: async (id) => (await api.get(`/partner-admin/requests/${id}/quote-prefill`, { headers: getAuthHeader() })).data,
  setRequestStatus: async (id, status) => (await api.post(`/partner-admin/requests/${id}/status`, { status }, { headers: getAuthHeader() })).data,
  addInternalNote: async (id, body) => (await api.post(`/partner-admin/requests/${id}/internal-note`, { body }, { headers: getAuthHeader() })).data,
  addPartnerMessage: async (id, body) => (await api.post(`/partner-admin/requests/${id}/message`, { body }, { headers: getAuthHeader() })).data,
  shareQuote: async (id, payload) => (await api.post(`/partner-admin/requests/${id}/share-quote`, payload, { headers: getAuthHeader() })).data,
  downloadRequestDoc: async (id, docId) => (await api.get(`/partner-admin/requests/${id}/documents/${docId}/download`, { headers: getAuthHeader() })).data,
};

// Partner Portal — external partner-facing (partner_user, company-scoped)
export const partnerPortalService = {
  me: async () => (await api.get('/partner/me', { headers: getAuthHeader() })).data,
  getCompany: async () => (await api.get('/partner/company', { headers: getAuthHeader() })).data,
  updateCompany: async (payload) => (await api.patch('/partner/company', payload, { headers: getAuthHeader() })).data,
  dashboard: async () => (await api.get('/partner/dashboard', { headers: getAuthHeader() })).data,
  listRequests: async () => (await api.get('/partner/requests', { headers: getAuthHeader() })).data,
  getRequest: async (id) => (await api.get(`/partner/requests/${id}`, { headers: getAuthHeader() })).data,
  submitRequest: async (payload) => (await api.post('/partner/requests', payload, { headers: getAuthHeader() })).data,
  editRequest: async (id, payload) => (await api.patch(`/partner/requests/${id}`, payload, { headers: getAuthHeader() })).data,
  listDocuments: async (id) => (await api.get(`/partner/requests/${id}/documents`, { headers: getAuthHeader() })).data,
  uploadDocument: async (id, formData) => (await api.post(`/partner/requests/${id}/documents`, formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } })).data,
  downloadDocument: async (id, docId) => (await api.get(`/partner/requests/${id}/documents/${docId}/download`, { headers: getAuthHeader() })).data,
  listMessages: async (id) => (await api.get(`/partner/requests/${id}/messages`, { headers: getAuthHeader() })).data,
  addMessage: async (id, body) => (await api.post(`/partner/requests/${id}/messages`, { body }, { headers: getAuthHeader() })).data,
  listQuotes: async () => (await api.get('/partner/quotes', { headers: getAuthHeader() })).data,
  getQuote: async (id) => (await api.get(`/partner/quotes/${id}`, { headers: getAuthHeader() })).data,
  acceptQuote: async (id) => (await api.post(`/partner/quotes/${id}/accept`, {}, { headers: getAuthHeader() })).data,
  declineQuote: async (id) => (await api.post(`/partner/quotes/${id}/decline`, {}, { headers: getAuthHeader() })).data,
};

export default api;